import React, {
  useState,
  useEffect,
  forwardRef,
  RefObject,
  SetStateAction,
  Dispatch,
  ReactNode,
} from 'react';
import { cn } from '@/utils/tailwind';
import { PullToRefreshSpinner } from './PullToRefreshSpinner';
import useWebview from '@/hooks/V3/useWebview';

interface PullToRefreshProps {
  onRefresh?: () => Promise<void>;
  className?: string;
  refreshing: boolean;
  setRefreshing: Dispatch<SetStateAction<boolean>>;
  children: ReactNode;
}

export const PullToRefresh = forwardRef<HTMLDivElement, PullToRefreshProps>(
  ({ refreshing, setRefreshing, onRefresh, className, children }, ref) => {
    const [startY, setStartY] = useState(0);
    const [isPulling, setIsPulling] = useState(false);
    const [pullDistance, setPullDistance] = useState(0);

    const [hintDistance, setHintDistance] = useState(0);

    const [hasTriggeredHaptic, setHasTriggeredHaptic] = useState(false);

    const { triggerHaptic } = useWebview();

    const THRESHOLD = 80;
    const MAX_DISTANCE = 120;
    const REFRESH_DISTANCE = 30;

    useEffect(() => {
      const currentRef = (ref as RefObject<HTMLDivElement>)?.current;
      if (!currentRef) return;

      const handleTouchStart = (event: TouchEvent) => {
        const target = event.target as HTMLElement;
        const isInsideBottomSheet = !!target.closest('.keyboard-avoid-bottom-sheet');

        if (isInsideBottomSheet || refreshing) return;

        const atTop = currentRef.scrollTop <= 0;
        const isContentShort = currentRef.scrollHeight <= currentRef.clientHeight;

        if (atTop || isContentShort) {
          setHintDistance(0);
          setStartY(event.touches[0].clientY);
          setIsPulling(true);
          setHasTriggeredHaptic(false);
        }
      };

      const handleTouchMove = (event: TouchEvent) => {
        if (!isPulling || refreshing) return;

        const target = event.target as HTMLElement;
        const isInsideBottomSheet = !!target.closest('.keyboard-avoid-bottom-sheet');
        if (isInsideBottomSheet) return;

        const moveY = event.touches[0].clientY;
        const distance = moveY - startY;

        const atTop = currentRef.scrollTop <= 0;
        const isContentShort = currentRef.scrollHeight <= currentRef.clientHeight;
        const atBottom =
          currentRef.scrollTop + currentRef.clientHeight >= currentRef.scrollHeight - 1;

        if (distance > 0 && atTop) {
          event.preventDefault();
          const dampenedDistance = Math.min(Math.pow(distance, 0.85), MAX_DISTANCE);
          setPullDistance(dampenedDistance);
          // THRESHOLD 도달 시 햅틱!
          if (dampenedDistance >= THRESHOLD && !hasTriggeredHaptic) {
            triggerHaptic('medium');
            setHasTriggeredHaptic(true);
          }
        } else if (distance < 0 && isContentShort && atBottom) {
          event.preventDefault();
          const dampenedDistance = Math.max(-MAX_DISTANCE, -Math.pow(Math.abs(distance), 0.85));
          setPullDistance(dampenedDistance);
        }
      };

      const handleTouchEnd = async () => {
        if (!isPulling) return;

        setIsPulling(false);

        if (pullDistance >= THRESHOLD) {
          setPullDistance(REFRESH_DISTANCE);
          setRefreshing(true);
          setHintDistance(0);

          const startTime = Date.now();

          if (onRefresh) {
            try {
              await onRefresh();
            } catch (error) {
              console.error('Error in onRefresh:', error);
            }
          }

          const elapsedTime = Date.now() - startTime;
          const delay = Math.max(0, 500 - elapsedTime);

          setTimeout(() => {
            setPullDistance(0);
            setTimeout(() => {
              setRefreshing(false);
            }, 300);
          }, delay);
        } else {
          setPullDistance(0);
        }
      };

      //  스크롤 이벤트를 감지하여 힌트를 표시하는 핸들러
      const handleScroll = () => {
        // 사용자가 당기고 있거나 새로고침 중일때는 힌트를 표시하지 않음
        if (isPulling || refreshing) {
          setHintDistance(0);
          return;
        }

        const top = currentRef.scrollTop;
        if (top < 0) {
          const dampenedHint = Math.min(Math.pow(Math.abs(top), 0.85), MAX_DISTANCE);
          setHintDistance(dampenedHint);
        } else {
          setHintDistance(0);
        }
      };

      currentRef.addEventListener('touchstart', handleTouchStart);
      currentRef.addEventListener('touchmove', handleTouchMove, { passive: false });
      currentRef.addEventListener('touchend', handleTouchEnd);

      currentRef.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        currentRef.removeEventListener('touchstart', handleTouchStart);
        currentRef.removeEventListener('touchmove', handleTouchMove);
        currentRef.removeEventListener('touchend', handleTouchEnd);

        currentRef.removeEventListener('scroll', handleScroll);
      };
    }, [refreshing, startY, isPulling, pullDistance, ref, onRefresh, setRefreshing]);

    const spinnerDistance = Math.max(pullDistance, hintDistance);
    const progress = Math.min((spinnerDistance / THRESHOLD) * 100, 100);

    return (
      <div
        ref={ref}
        className={cn('overscroll-behavior-contain relative h-full overflow-y-auto', className)}
      >
        <div
          className="h-full"
          style={{
            transform: `translateY(${pullDistance}px)`,
            transition: isPulling ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {spinnerDistance > 0 && (
            <div
              className="absolute left-1/2 -translate-x-1/2 transition-opacity"
              style={{
                top: `-20px`,
                opacity: spinnerDistance > 20 ? 1 : spinnerDistance / 20,
              }}
            >
              <PullToRefreshSpinner progress={refreshing ? 100 : progress} />
            </div>
          )}
          {children}
        </div>
      </div>
    );
  },
);

PullToRefresh.displayName = 'PullToRefresh';
